import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

type TierSlug = string // accepts any string, handles known ones with color

interface TierBadgeProps {
  tier: TierSlug | null | undefined
  size?: 'sm' | 'md'
  className?: string
}

/**
 * Single source of truth for tier chip styling across admin + user-facing UIs.
 * Associate = sky (entry), Partner = brand royal (core), Principal = violet
 * (premium), Trial = muted outline, Enterprise = emerald (custom). Unknown /
 * legacy tiers (starter/professional) fall through to neutral outline.
 */
export function TierBadge({ tier, size = 'md', className }: TierBadgeProps) {
  const normalized = (tier || 'trial').toLowerCase()
  const label =
    normalized === 'trial'
      ? 'Trial'
      : normalized.charAt(0).toUpperCase() + normalized.slice(1)

  const tone: Record<string, string> = {
    associate: 'bg-sky-500 hover:bg-sky-500 text-white border-transparent',
    partner: 'bg-brand-royal hover:bg-brand-royal text-white border-transparent',
    principal: 'bg-violet-600 hover:bg-violet-600 text-white border-transparent',
    enterprise: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent',
    trial: 'bg-transparent text-muted-foreground border-border',
  }
  const toneClass = tone[normalized] ?? 'bg-transparent text-muted-foreground border-border'

  return (
    <Badge
      variant="outline"
      className={cn(
        'capitalize whitespace-nowrap',
        size === 'sm' ? 'text-[10px] px-1.5 py-0 h-5' : 'text-xs',
        toneClass,
        className
      )}
    >
      {label}
    </Badge>
  )
}

/**
 * Standard per-tier monthly price in cents. Keeps the tier → price map in one
 * place so the admin Monthly column, sidebar footer, and anywhere else all
 * agree. Custom negotiated prices override this via organizations.billing_custom_monthly_cents.
 */
export const STANDARD_TIER_MONTHLY_CENTS: Record<string, number> = {
  associate: 39700,
  partner: 89700,
  principal: 149700,
}

export function formatMonthly(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || cents === 0) return '$0'
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
